import React, { useState, useCallback } from "react";
import { RiseUserAction } from "@/types/RiseUserAction";
import { ActionGroupBadge } from "../ActionGroupBadge";
import { useTranslation } from "react-i18next";
import { ActionStudentsTable } from "./ActionStudentsTable";
import { Metrics } from "./Metrics";
import { BulkStatusUpdateDropdown } from "./BulkStatusUpdateDropdown";
import { useMutation, useApolloClient } from "@apollo/client";
import { toast } from "sonner";
import {
  UpdateRiseActionStatusesMutation,
  UpdateRiseActionStatusesMutationVariables,
  RiseUserActionStudentStatusEnum,
} from "@/__generated__/types";
import { UPDATE_RISE_ACTION_STATUSES } from "@/api/mutations/updateRiseActionStatuses";
import { USER_ACTION_METRICS } from "@/api/queries/userActionMetrics";
import { noop } from "lodash";
import { Student } from "@/types/Student";
import { Deselect } from "./Deselect";
import { Button } from "../ui";
import { RiseActionGroupMasterID } from "@/types/RiseActionGroupMasterID";
import { ProtectedNavLink } from "../ProtectedNavigationLink";
import { useBuildUrlState } from "@/hooks/useUrlSerializer";

const RISE_STUDENT_STATUS_GRAPH_TYPENAME = "RiseUserActionStudentStatus";

export const RiseActionDetails = ({ action }: { action: RiseUserAction }) => {
  const { t } = useTranslation();
  const client = useApolloClient();
  const [selectedStudents, setSelectedStudents] = useState<Student[]>([]);
  const [bulkUpdateStatus, setBulkUpdateStatus] =
    useState<RiseUserActionStudentStatusEnum | null>(null);

  const [updateStudentStatuses, { loading }] = useMutation<
    UpdateRiseActionStatusesMutation,
    UpdateRiseActionStatusesMutationVariables
  >(UPDATE_RISE_ACTION_STATUSES, {
    refetchQueries: [USER_ACTION_METRICS],
    awaitRefetchQueries: true,
    onCompleted: () => {
      toast(t("riseActionDetails.messages.statusesSuccess"), {
        description: t("riseActionDetails.messages.statusesUpdated", {
          count: selectedStudents.length,
        }),
        action: {
          label: t("riseActionDetails.messages.close"),
          onClick: noop,
        },
      });
      if (bulkUpdateStatus) {
        for (const id of selectedStudents
          .map(
            (student) =>
              student.studentOrganizationStatus?.rise
                ?.riseUserActionStudentStatus?.id,
          )
          .filter((id) => id !== undefined)) {
          client.cache.modify({
            id: `${RISE_STUDENT_STATUS_GRAPH_TYPENAME}:${id}`,
            fields: {
              status() {
                return bulkUpdateStatus;
              },
            },
          });
        }
      }
      setSelectedStudents([]);
    },
    onError: () => {
      toast.error(t("riseActionDetails.messages.error"), {
        description: t("riseActionDetails.messages.statusesError"),
        action: {
          label: t("riseActionDetails.messages.close"),
          onClick: noop,
        },
      });
    },
  });

  const isBulkMessageAction =
    action.actionGroup.masterId === RiseActionGroupMasterID.BULK_MESSAGE;

  const handleUpdateStudentStatus = useCallback(
    (status: RiseUserActionStudentStatusEnum) => {
      setBulkUpdateStatus(status);
      updateStudentStatuses({
        variables: {
          riseActionId: action.id,
          status,
          ids: selectedStudents
            .map(
              (student) =>
                student.studentOrganizationStatus?.rise
                  ?.riseUserActionStudentStatus?.id,
            )
            .filter((id) => id !== undefined),
        },
      });
    },
    [selectedStudents, action.id, updateStudentStatuses],
  );

  const shareUrlQuery = useBuildUrlState("BULK_MESSAGE_COMPOSE")({
    riseActionId: action.id,
  });

  return (
    <>
      <div className="mb-6 flex rounded-lg border">
        <div className="grow py-[10px]">
          <div className="p-4">
            <div className="flex gap-2 font-semibold">
              {action.actionType.displayName}
              <ActionGroupBadge actionGroup={action.actionGroup} />
            </div>
            <div className="mt-2 text-muted-foreground">
              {action.actionType.description}
            </div>
          </div>
        </div>
        <Metrics action={action} />
      </div>
      <div className="mb-2 flex justify-end">
        {!!Object.keys(selectedStudents).length && (
          <div className="mr-auto flex items-center gap-2">
            <BulkStatusUpdateDropdown
              onSelect={handleUpdateStudentStatus}
              loading={loading}
            />
            <button onClick={() => setSelectedStudents([])}>
              <Deselect />
            </button>
          </div>
        )}

        {isBulkMessageAction && (
          <div className="flex items-center gap-2">
            <Button asChild>
              <ProtectedNavLink
                route={"bulkMessageBasicInfo"}
                query={shareUrlQuery}
              >
                {t("riseActionDetails.startBulkMessage")}
              </ProtectedNavLink>
            </Button>
          </div>
        )}
      </div>
      <div className="overflow-hidden">
        <ActionStudentsTable
          action={action}
          selectedStudents={selectedStudents}
          onSelectedStudentsChange={setSelectedStudents}
          disabledStudentRowIds={
            loading ? selectedStudents.map((student) => student.id || "") : []
          }
        />
      </div>
    </>
  );
};
